/**
 * #2058 (TV steering surface) — a THIN re-export + two pure render helpers over
 * the real `@volley/crucible-ipc-types` contract. The `pick-one` stage shape
 * lives on the real `AssetReviewStage` / `AssetReviewSnapshot` (`mode` +
 * `candidates`); the pick is sent via `art:command` (crucible#3112 — the same
 * channel every stage uses), so there is no separate pick payload type here.
 *
 * Kept as a single import surface for the steering component so the consuming
 * code stays decoupled from the exact ipc-types module path, and so the pure
 * helpers (`isPickOneStage`, `candidateImageUrl`) have one testable home.
 */

import type {
  AssetReviewCommandResult,
  AssetReviewItem,
  AssetReviewSnapshot,
  AssetReviewStage,
  MoodboardCandidate,
} from '@volley/crucible-ipc-types';

export type { MoodboardCandidate } from '@volley/crucible-ipc-types';

/**
 * A stage in `pick-one` mode. The real `AssetReviewStage` already carries the
 * optional `mode` + `candidates` fields, so this is just a readable alias for
 * the steering surface (a moodboard stage is an `AssetReviewStage` with
 * `mode === 'pick-one'`).
 */
export type MoodboardPickStage = AssetReviewStage;

/** The review snapshot the steering surface renders (real shape; its `stages`
 *  carry `mode`/`candidates` when a pick-one stage is active). */
export type SteeringSnapshot = AssetReviewSnapshot;

/**
 * Is THIS stage a pick-one moodboard with a candidate set to choose from?
 * Pure + exported for unit testing the render switch without mounting.
 */
export function isPickOneStage(
  stage: AssetReviewStage | null | undefined,
): stage is AssetReviewStage & { candidates: MoodboardCandidate[] } {
  if (!stage) return false;
  return (
    stage.mode === 'pick-one' && Array.isArray(stage.candidates) && stage.candidates.length > 0
  );
}

/** The renderer-loadable URL for a candidate (a `game-asset-review://…` proxy
 *  or a `data:` URI), or null while the image is still generating. */
export function candidateImageUrl(candidate: MoodboardCandidate): string | null {
  return candidate.imageUrl ?? null;
}

// ── crucible#3366 — concurrent-apply reconciliation ─────────────────────────────
//
// crucible#3361 made per-card asset-review commands CONCURRENT (feedback on card
// B no longer waits for card A to finish regenerating). Each command resolves
// with its OWN full `buildSnapshot`, and the surface used to `apply()` whichever
// resolved last, wholesale. A slower response whose snapshot was built from an
// OLDER server state can resolve AFTER a sibling write and briefly roll that
// sibling back to its pre-write state until the next 1.5s poll self-heals it.
//
// The fix below is two independent, order-insensitive rules:
//   1. Per-asset merge by id (`mergeAssetsById`): a command is authoritative
//      ONLY for the asset id(s) it targeted; every other asset keeps its current
//      value. So A's stale view of B can never overwrite B's fresher status.
//   2. Stage-level fields (counts / `stages[]` / `currentStageIndex` / `done` /
//      `complete` / `finished`) aren't per-asset, so they can't be merged by id.
//      They come from whichever response was ISSUED against the most-recent
//      server state, tracked by a monotonic issue-order sequence — NOT wall-clock
//      or resolution order, since a later-resolving promise can carry older data.

/** The set of asset ids a resolved response is authoritative for, or `null` when
 *  it is authoritative for the WHOLE snapshot (a poll, a stage-wide command, or
 *  a first load with no prior state to merge onto). */
export type OwnedAssetIds = ReadonlySet<string> | null;

/** Which asset ids a resolved `art:command` response owns.
 *   - a rejected command (`ok: false`, intent `none`) changed nothing → owns the
 *     empty set, so it can refresh stage-level fields without touching any asset.
 *   - a single-target `approve` / `regenerate` → owns exactly that target.
 *   - a stage-wide `approve-stage` / `regenerate-stage` → `null` (whole snapshot).
 *     These never overlap a per-card command (the surface's in-flight guards
 *     forbid it), so treating them as fully authoritative is safe. */
export function ownedAssetIdsFromCommandResult(result: AssetReviewCommandResult): OwnedAssetIds {
  if (!result.ok) return new Set();
  switch (result.intent.kind) {
    case 'approve':
    case 'regenerate':
      return new Set([result.intent.targetId]);
    case 'approve-stage':
    case 'regenerate-stage':
      return null;
  }
}

/**
 * Fold a resolved snapshot's assets into the current list, keyed by id: for each
 * CURRENT asset, take the incoming version only if its id is `owned` (and the
 * incoming snapshot still has it), otherwise keep the current one; then append
 * any brand-new incoming assets the current list didn't have yet. Preserves the
 * current ordering. Order-insensitive: applying A-then-B and B-then-A yield the
 * same per-asset result, which is exactly what makes a stale, later-resolving
 * sibling response unable to revert a fresher one.
 */
export function mergeAssetsById(
  current: readonly AssetReviewItem[],
  incoming: readonly AssetReviewItem[],
  owned: ReadonlySet<string>,
): AssetReviewItem[] {
  const incomingById = new Map(incoming.map((a) => [a.id, a]));
  const currentIds = new Set(current.map((a) => a.id));
  const merged = current.map((a) => {
    const inc = incomingById.get(a.id);
    return owned.has(a.id) && inc ? inc : a;
  });
  for (const a of incoming) {
    if (!currentIds.has(a.id)) merged.push(a);
  }
  return merged;
}

/** Snapshot + the issue-order sequence whose stage-level fields it currently
 *  shows. Held by the surface across resolves so out-of-order responses can be
 *  reconciled deterministically. */
export interface SteeringApplyState {
  snapshot: SteeringSnapshot | null;
  /** Issue-order seq of the response whose stage-level fields are shown. */
  stageSeq: number;
}

/**
 * Reconcile a freshly-resolved snapshot into the current one (crucible#3366):
 *
 *   - Per-asset: `owned === null` → the response is authoritative for the whole
 *     asset list (wholesale). Otherwise fold by id via `mergeAssetsById`, so
 *     siblings the response didn't target keep their current value.
 *   - Stage-level: taken from the response only if it was issued against an
 *     at-least-as-recent server state (`issueSeq >= state.stageSeq`); a
 *     later-resolving but earlier-issued (staler) response keeps the current
 *     stage-level fields instead of rolling them back.
 *
 * Returns the merged snapshot plus the new `stageSeq` to carry forward.
 */
export function reconcileSteeringSnapshot(
  state: SteeringApplyState,
  incoming: SteeringSnapshot,
  owned: OwnedAssetIds,
  issueSeq: number,
): { snapshot: SteeringSnapshot; stageSeq: number } {
  const current = state.snapshot;
  // No prior snapshot to merge onto — the incoming one is the whole truth.
  if (current === null) return { snapshot: incoming, stageSeq: issueSeq };
  const takeIncomingStage = issueSeq >= state.stageSeq;
  const stageSeq = takeIncomingStage ? issueSeq : state.stageSeq;
  const assets =
    owned === null ? incoming.assets : mergeAssetsById(current.assets, incoming.assets, owned);
  const stageBase = takeIncomingStage ? incoming : current;
  return { snapshot: { ...stageBase, assets }, stageSeq };
}
